/**
 * The figures of round 3 (12 Oct 2026): small pictures built from DS components and the
 * icons of `lib/line-icons.ts`, which the training (`{ kind: "figure" }` blocks), the
 * Help page, the quick start, the cheat sheet and the queue help pop-up show. The
 * registry maps a figure name of the content to its component; every figure takes an
 * optional caption and nothing else.
 *
 * The ten documentation figures (concept drawings for `/docs`, 12 Oct 2026) follow the
 * same contract and are listed after the five of round 3.
 */

import type { ComponentType } from "react";

import { AutonomyGatesFigure } from "./autonomy-gates";
import { ContainersAndJobsFigure } from "./containers-and-jobs";
import { CustomerResolutionLadderFigure } from "./customer-resolution-ladder";
import { DataStoresAndSyncsFigure } from "./data-stores-and-syncs";
import { FrontendStructureFigure } from "./frontend-structure";
import { HowItWorks } from "./how-it-works";
import { ItemMatchingLadderFigure } from "./item-matching-ladder";
import { MailToDraftPipelineFigure } from "./mail-to-draft-pipeline";
import { QueueRowFigure } from "./queue-row-figure";
import { RepoMapFigure } from "./repo-map";
import { RequestPathFigure } from "./request-path";
import { StatusLegendFigure } from "./status-legend";
import { SystemContextFigure } from "./system-context";
import { TableRowFigure } from "./table-row-figure";
import { ThreeSteps } from "./three-steps";

/** The figures of the training and the help (round 3). */
export type SalesFigureName =
  | "how-it-works"
  | "status-legend"
  | "three-steps"
  | "table-row"
  | "queue-row";

/** The concept drawings of the documentation (`/docs`). */
export type DocsFigureName =
  | "system-context"
  | "containers-and-jobs"
  | "mail-to-draft-pipeline"
  | "data-stores-and-syncs"
  | "customer-resolution-ladder"
  | "item-matching-ladder"
  | "autonomy-gates"
  | "frontend-structure"
  | "request-path"
  | "repo-map";

export type FigureName = SalesFigureName | DocsFigureName;

export const DOCS_FIGURE_NAMES: readonly DocsFigureName[] = [
  "system-context",
  "containers-and-jobs",
  "mail-to-draft-pipeline",
  "data-stores-and-syncs",
  "customer-resolution-ladder",
  "item-matching-ladder",
  "autonomy-gates",
  "frontend-structure",
  "request-path",
  "repo-map",
];

export const FIGURES: Record<FigureName, ComponentType<{ caption?: string }>> = {
  "how-it-works": HowItWorks,
  "status-legend": StatusLegendFigure,
  "three-steps": ThreeSteps,
  "table-row": TableRowFigure,
  "queue-row": QueueRowFigure,
  "system-context": SystemContextFigure,
  "containers-and-jobs": ContainersAndJobsFigure,
  "mail-to-draft-pipeline": MailToDraftPipelineFigure,
  "data-stores-and-syncs": DataStoresAndSyncsFigure,
  "customer-resolution-ladder": CustomerResolutionLadderFigure,
  "item-matching-ladder": ItemMatchingLadderFigure,
  "autonomy-gates": AutonomyGatesFigure,
  "frontend-structure": FrontendStructureFigure,
  "request-path": RequestPathFigure,
  "repo-map": RepoMapFigure,
};

export { AUTONOMY_GATES_TITLE, AutonomyGatesFigure } from "./autonomy-gates";
export { CONTAINERS_AND_JOBS_TITLE, ContainersAndJobsFigure } from "./containers-and-jobs";
export {
  CUSTOMER_RESOLUTION_LADDER_TITLE,
  CustomerResolutionLadderFigure,
} from "./customer-resolution-ladder";
export { DATA_STORES_AND_SYNCS_TITLE, DataStoresAndSyncsFigure } from "./data-stores-and-syncs";
export { DRAFT_FIGURE_BADGE } from "./docs-placeholder";
export { FRONTEND_STRUCTURE_TITLE, FrontendStructureFigure } from "./frontend-structure";
export { HOW_IT_WORKS_TITLE, HowItWorks, STATIONS } from "./how-it-works";
export { ITEM_MATCHING_LADDER_TITLE, ItemMatchingLadderFigure } from "./item-matching-ladder";
export { MAIL_TO_DRAFT_PIPELINE_TITLE, MailToDraftPipelineFigure } from "./mail-to-draft-pipeline";
export { EXAMPLE_QUEUE_ROW, QUEUE_ROW_TITLE, QueueRowFigure } from "./queue-row-figure";
export { REPO_MAP_TITLE, RepoMapFigure } from "./repo-map";
export { REQUEST_PATH_TITLE, RequestPathFigure } from "./request-path";
export {
  CHEAT_LEGEND_ICONS,
  COMPACT_LINES,
  type LegendItem,
  STATUS_LEGEND_ITEMS,
  STATUS_LEGEND_TITLE,
  StatusLegend,
  StatusLegendFigure,
} from "./status-legend";
export { SYSTEM_CONTEXT_TITLE, SystemContextFigure } from "./system-context";
export { EXAMPLE_LINE, TABLE_ROW_TITLE, TableRowFigure } from "./table-row-figure";
export { STEP_PLACES, THREE_STEPS_TITLE, ThreeSteps } from "./three-steps";
